import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { BookOpen, Map, Play, Lightbulb, Book, BrainCircuit } from 'lucide-react';

interface Category {
  id: string;
  slug: string;
  name: string;
  icon: string;
  color: string;
}

interface CategoryPillsProps {
  categories: Category[];
  className?: string;
}

// Map of icon names to icon components
const iconMap: Record<string, React.ReactNode> = {
  'book-open': <BookOpen className="h-4 w-4" />,
  'map': <Map className="h-4 w-4" />,
  'play': <Play className="h-4 w-4" />,
  'lightbulb': <Lightbulb className="h-4 w-4" />,
  'book': <Book className="h-4 w-4" />,
  'brain': <BrainCircuit className="h-4 w-4" />,
};

// Map of colors to Tailwind classes
const colorMap: Record<string, { bg: string, text: string, gradientFrom: string, gradientTo: string }> = {
  'blue': {
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    text: 'text-blue-600 dark:text-blue-300',
    gradientFrom: 'from-blue-500',
    gradientTo: 'to-blue-400'
  },
  'green': {
    bg: 'bg-green-100 dark:bg-green-900/40',
    text: 'text-green-600 dark:text-green-300',
    gradientFrom: 'from-green-500',
    gradientTo: 'to-green-400'
  },
  'purple': {
    bg: 'bg-purple-100 dark:bg-purple-900/40',
    text: 'text-purple-600 dark:text-purple-300',
    gradientFrom: 'from-purple-500',
    gradientTo: 'to-purple-400'
  },
  'amber': {
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    text: 'text-amber-600 dark:text-amber-300',
    gradientFrom: 'from-amber-500',
    gradientTo: 'to-amber-400'
  },
  'red': {
    bg: 'bg-red-100 dark:bg-red-900/40',
    text: 'text-red-600 dark:text-red-300',
    gradientFrom: 'from-red-500',
    gradientTo: 'to-red-400'
  },
  'indigo': {
    bg: 'bg-indigo-100 dark:bg-indigo-900/40',
    text: 'text-indigo-600 dark:text-indigo-300',
    gradientFrom: 'from-indigo-500',
    gradientTo: 'to-indigo-400'
  },
};

const CategoryPills: React.FC<CategoryPillsProps> = ({
  categories,
  className,
}) => {
  const navigate = useNavigate();
  
  // Handle category click
  const handleCategoryClick = (slug: string) => {
    navigate(`/courses/category/${slug}`);
  };
  
  return (
    <div className={cn(
      "grid grid-cols-3 gap-3",
      className
    )}>
      {categories.map((category) => {
        const colorStyle = colorMap[category.color] || colorMap.blue;
        const icon = iconMap[category.icon] || <Book className="h-4 w-4" />;
        
        return (
          <div
            key={category.id}
            className={cn(
              "rounded-2xl p-3 flex flex-col items-center gap-2 cursor-pointer touch-feedback",
              "border border-transparent transition-colors",
              colorStyle.bg
            )}
            onClick={() => handleCategoryClick(category.slug)}
          >
            <div className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center",
              "bg-gradient-to-br",
              colorStyle.gradientFrom,
              colorStyle.gradientTo,
              "text-white"
            )}>
              {icon}
            </div>
            <span className={cn(
              "text-xs font-medium text-center line-clamp-2",
              colorStyle.text
            )}>
              {category.name}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default CategoryPills; 